import { useMemo } from 'react';
import { usePPPLocale } from '../../usePPPLocale';
import { resolveRezaPersonRecapRows } from '@gov-nx/core/hooks';
import { usePPPTypesQuery } from './usePPPTypesQuery';
import { phoneShapeReadable, toDateReadable } from '@gov-nx/utils/common';
import {
	formatDateRange,
	resolvePPPSelectLabel,
	resolveRecapOvmName,
} from '../utils';
import type {
	UsePPPZapisRecapValuesProps,
	UseZapisRecapValues,
} from './types/usePPPZapisRecapValues.types';

export const usePPPZapisRecapValues = ({
	formData,
	personIdentity,
}: UsePPPZapisRecapValuesProps): UseZapisRecapValues => {
	const { t } = usePPPLocale();
	const { options } = usePPPTypesQuery();

	return useMemo(() => {
		const personRows = resolveRezaPersonRecapRows(personIdentity, {
			prijmeni: t('zapis.form.step3.moje-udaje.rekapitulace.prijmeni'),
			jmeno: t('zapis.form.step3.moje-udaje.rekapitulace.jmeno'),
			datumNarozeni: t(
				'zapis.form.step3.moje-udaje.rekapitulace.datum-narozeni'
			),
			adresaTrvalehoBydliste: t(
				'zapis.form.step3.moje-udaje.rekapitulace.adresa-trvaleho-bydliste'
			),
			nazev: t('zapis.form.step3.moje-udaje.rekapitulace.nazev'),
			ico: t('zapis.form.step3.moje-udaje.rekapitulace.ico'),
			adresaSidla: t('zapis.form.step3.moje-udaje.rekapitulace.adresa-sidla'),
		});

		return {
			step1: [
				...personRows,
				{
					label: t('zapis.form.step3.moje-udaje.rekapitulace.e-mail'),
					value: formData._useEmail ? formData.email : undefined,
				},
				{
					label: t('zapis.form.step3.moje-udaje.rekapitulace.telefon'),
					value:
						formData._usePhone && formData.phone
							? phoneShapeReadable(formData.phone)
							: undefined,
				},
			],
			step2: [
				{
					label: t('zapis.form.step3.detail-zadosti.rekapitulace.typ-PPP'),
					value: resolvePPPSelectLabel(formData.PPPSelect, options, t),
				},
				{
					label: t(
						'zapis.form.step3.detail-zadosti.rekapitulace.prislusne-OVM'
					),
					value: resolveRecapOvmName(formData),
				},
				{
					label: t(
						'zapis.form.step3.detail-zadosti.rekapitulace.vydany-dokument'
					),
					children: [
						{
							label: t(
								'zapis.form.step3.detail-zadosti.rekapitulace.cislo-dokumentu'
							),
							value: formData.number,
						},
						{
							label: t(
								'zapis.form.step3.detail-zadosti.rekapitulace.datum-vydani'
							),
							value: formData.issueDate
								? toDateReadable(formData.issueDate)
								: undefined,
						},
						{
							label: t(
								'zapis.form.step3.detail-zadosti.rekapitulace.platnost-od-do'
							),
							value: formatDateRange(
								formData.datumOd,
								formData.datumDo,
								t('zapis.form.step3.message.nevybrano')
							),
						},
					],
				},
				{
					label: t('zapis.form.step3.detail-zadosti.rekapitulace.priloha'),
					value: formData.fileUpload,
				},
			],
		};
	}, [formData, options, personIdentity, t]);
};
